import { asset } from "./assets";

/** The three Sadguru bio page slugs, matching `public/_redirects` targets. */
export type SadguruSlug =
  | "sadguru-sadafaldeo-ji-maharaj"
  | "sadguru-dharmchandradeo-ji-maharaj"
  | "sadguru-swatantradeo-ji-maharaj";

interface Portrait {
  src: string;
  alt: string;
}

interface LocalizedText {
  en: string;
  hi: string;
}

// Not exported: nothing outside this module needs the per-card shape, only
// the assembled `INSPIRATION` value below (fallow's dead-code check flags
// an exported type with no importer, even one used only via `satisfies`
// in the same file).
interface InspirationCard {
  slug: SadguruSlug;
  href: string;
  portrait: Portrait;
  name: LocalizedText;
  blurb: LocalizedText;
}

interface LivingSaintCard {
  name: LocalizedText;
  blurb: LocalizedText;
  portrait: Portrait;
  href: string;
}

/**
 * One block of a bio's prose. `heading` marks the old site's inline
 * subheadings (e.g. "On a mission"); `verse` is a Sanskrit/Swarved couplet
 * or stanza, kept as separate lines with its optional source attribution.
 */
export type BioBlock =
  | { kind: "heading"; text: string }
  | { kind: "paragraph"; text: string }
  | { kind: "verse"; lines: readonly string[]; attribution?: string };

interface BioContent {
  /** The saint's name, verbatim from the old site's portrait caption. */
  heading: string;
  portrait: Portrait;
  body: readonly BioBlock[];
}

// Not exported, same reason as InspirationCard/LivingSaintCard above.
interface SadguruBio {
  en: BioContent;
  hi: BioContent;
}

const sadafaldeoPortrait: Portrait = {
  src: asset(
    "https://site-hjx4qqt6.wsecdn1.websitecdn.com/uploads/480-444/A00B6AF75FD8059F.jpg?v=250703010154",
  ),
  alt: "Portrait of Sadguru Sadafal Deo Ji Maharaj",
};

const dharmchandradeoPortrait: Portrait = {
  src: asset(
    "https://site-hjx4qqt6.wsecdn1.websitecdn.com/uploads/480-444/3022188EAC1C22A7.jpg?v=250703010154",
  ),
  alt: "Portrait of Sadguru Dharmachandra Deo Ji Maharaj",
};

const swatantradeoPortrait: Portrait = {
  src: asset(
    "https://site-hjx4qqt6.wsecdn1.websitecdn.com/uploads/480-444/CD796FE7DC940D7D.jpg?v=250703010154",
  ),
  alt: "Portrait of Sadguru Swatantra Deo Ji Maharaj",
};

const vigyandeoPortrait: Portrait = {
  src: asset(
    "https://site-hjx4qqt6.wsecdn1.websitecdn.com/uploads/623-252/1665984D16977ACA.jpg?v=250703010154",
  ),
  alt: "Portrait of Sant Pravar Vigyan Deo Ji Maharaj",
};

const naamdeoPortrait: Portrait = {
  src: asset(
    "https://site-hjx4qqt6.wsecdn1.websitecdn.com/uploads/623-252/FD49DA3A4CE65544.jpg?v=250703010154",
  ),
  alt: "Portrait of Sant Shri Naam Deo Ji Maharaj",
};

/** `/inspiration` and `/hi/inspiration`, verbatim from the old site. */
export const INSPIRATION = {
  heading: "Sadguru & Successors",
  cards: [
    {
      slug: "sadguru-sadafaldeo-ji-maharaj",
      href: "/inspiration/sadguru-sadafaldeo-ji-maharaj",
      portrait: sadafaldeoPortrait,
      name: {
        en: "Sadguru Sadafal Deo Ji Maharaj",
        hi: "सद्गुरु सदाफलदेव जी महाराज",
      },
      blurb: {
        en: "After his 17 years of penance, the Eternal Master blessed him with the supreme authority of Sadguru. Established Vihangam Yoga in 1924 with the sacred mission of spreading the Knowledge of Brahm.",
        hi: "17 वर्षों की तपस्या के बाद, नित्य अनादि सद्गुरु ने उन्हें सद्गुरु की सर्वोच्च शक्ति प्रदान की। ब्रह्मज्ञान के प्रसार के पवित्र प्रचार के साथ 1924 में विहंगम योग की स्थापना की।",
      },
    },
    {
      slug: "sadguru-dharmchandradeo-ji-maharaj",
      href: "/inspiration/sadguru-dharmchandradeo-ji-maharaj",
      portrait: dharmchandradeoPortrait,
      name: {
        en: "Sadguru Dharmachandra Deo Ji Maharaj",
        hi: "सद्गुरु धर्मचन्द्रदेव जी महाराज",
      },
      blurb: {
        en: 'Carried forward the Divine Light for 15 years. Wrote commentary on the book composed by Sadguru Sadafal Deo Ji Maharaj - "Swarved", the encyclopedia of spirituality for the mankind benefits,',
        hi: '15 वर्षों तक ईश्वरीय प्रकाश को आगे बढ़ाया। सद्गुरु सदाफलदेव जी महाराज द्वारा रचित ग्रन्थ - "स्वर्वेद" पर मानव जाति के लाभ के लिए अध्यात्म का विश्वकोश, भाष्य लिखा।',
      },
    },
    {
      slug: "sadguru-swatantradeo-ji-maharaj",
      href: "/inspiration/sadguru-swatantradeo-ji-maharaj",
      portrait: swatantradeoPortrait,
      name: {
        en: "Sadguru Swatantra Deo Ji Maharaj",
        hi: "सद्गुरु स्वतंत्रदेव जी महाराज",
      },
      blurb: {
        en: "Assumed the authority of Sadguru at age of 22 years. Since 50+ years, He is on the mission to fulfill the pledge of Sadguru Sadafal Deo Ji Maharaj - to spread the Knowledge of Brahm to the world.",
        hi: "22 वर्ष की आयु में सद्गुरु की सत्ता ग्रहण की। 50 से अधिक वर्षों से वे सद्गुरु सदाफलदेव जी महाराज की प्रतिज्ञा को पूरा करने के मिशन पर हैं - ब्रह्मज्ञान को विश्व में फैलाने के लिए।",
      },
    },
  ],
  livingSaints: [
    {
      name: {
        en: "Sant Pravar Vigyan Deo Ji Maharaj",
        hi: "संत प्रवर विज्ञानदेव जी महाराज",
      },
      blurb: {
        en: 'A living example of an enlightened soul. He is an experienced Yogi who has already lived through all spiritual experiences mentioned in "Swarved".',
        hi: "एक प्रबुद्ध आत्मा का जीवंत उदाहरण। वह एक अनुभवी योगी हैं जो पहले से ही “स्वर्वेद” में वर्णित सभी आध्यात्मिक अनुभवों से गुजर चुके हैं।",
      },
      portrait: vigyandeoPortrait,
      href: "https://www.facebook.com/SantPravar",
    },
    {
      name: {
        en: "Sant Shri Naam Deo Ji Maharaj",
        hi: "संत श्री नामदेव जी महाराज",
      },
      blurb: {
        en: "A living Saint and the President of International wing of Vihangam Yoga transforms the prospective with his mystical insights & the wisdom from the universe.",
        hi: "एक जीवित संत और विहंगम योग के अंतर्राष्ट्रीय विंग के अध्यक्ष अपनी रहस्यमय अंतर्दृष्टि और ब्रह्मांड के ज्ञान के साथ भविष्य को बदल देते हैं।",
      },
      portrait: naamdeoPortrait,
      href: "https://www.facebook.com/TheNaamdeo",
    },
  ],
} satisfies {
  heading: string;
  cards: readonly InspirationCard[];
  livingSaints: readonly LivingSaintCard[];
};

/**
 * The three Sadguru biographies, fetched verbatim from
 * `https://www.vihangamsg.com/sadguru-*-ji-maharaj` (`en`) and its `-HI`
 * twin (`hi`) - both are live on the old site. Paragraph breaks and inline
 * subheadings match each source; the Sanskrit/Swarved couplets are kept as
 * `verse` blocks with their own line breaks and attribution where the old
 * site gave one.
 *
 * The Hindi pages' own markup sometimes runs several English-side
 * paragraphs together as one `<br>`-separated block instead of the
 * `en` page's paragraph-per-block split; where that happens the `hi` body
 * has fewer blocks than `en` even though no sentence is missing, and a
 * comment at the merge says so.
 */
export const BIOS = {
  "sadguru-sadafaldeo-ji-maharaj": {
    en: {
      heading: "Himalaya Yogi Sadguru Sadafal Deo Ji Maharaj",
      portrait: sadafaldeoPortrait,
      body: [
        {
          kind: "paragraph",
          text: "Maharshi Sadguru Sadafal Deo Ji Maharaj was born in the year 1888 on the fourth day of the first fortnight of Bhadrapada, of Vikram Samvat 1945.",
        },
        {
          kind: "paragraph",
          text: "He was born in a renowned yogi family in the lineage of Maharishi Shringi . His forefather, Baba Lalji Rao, six generations above, was a great yogi who used to sit in a firm yogic posture in meditation for six months continuously.",
        },
        {
          kind: "heading",
          text: '"Journey of Sadguru Sadafal Deo Ji Maharaj" :—',
        },
        {
          kind: "paragraph",
          text: "Morning shows the day and the child shows the man. This proverb exactly fits the case of Maharishi Sadguru Sadafal Deo Ji Maharaj, while still a child of two; he sat aloof on a wooden plank and his father, mother and other elderly members of his house to lay prostrate before him, as he was a great saint. Persons thought that the child was just making fun and they reverently touched his feet. The same boy when began going to school could continue his studies. He wanted to learn Sanskrit in order to know the secret of God realization by going through the ancient scriptures.",
        },
        {
          kind: "paragraph",
          text: "At the early age of eleven Sadafal went to Kashi to learn Sanskrit, but the scholars of Sanskrit in Kasi, who were Brahmins by caste, did not agree to impart him the knowledge of Sanskrit, as he hailed from a Kshatriya family. Being disappointed, boy Sadafal decided to practice Yoga, because he had heard that ancient saints and seers got invaluable and deep knowledge of every thing by practicing Yoga. As a result he sat in meditation practicing ‘Unmunimudra’ which Lord Krishna has taught in the Gita. Maharishi Sadguru Sadafal Deo practiced his first lesson of yoga for controlling mind continuously for three years. He sat in meditation in a secluded place in Sidhasana, a yogic posture, for six hours every day.",
        },
        {
          kind: "paragraph",
          text: "One night while he was meditating thus, he found that his room was filled up with effulgence. He also heard a sweet and soothing voice from the void. “This won’t lead you to attain the Supreme Lord, as this practice is based on material sense organs.” Sometimes while in a state of meditation, he felt that someone put one of the fingers on the uppermost point of his head. On opening his eyes, he found none there. He could not make out who had spoken so sweetly and who had touched his head with his finger. Later on when Swamiji began practicing yoga inside his cave at Vritikoot Ashram, Pakari, he met a saint with light effulging from him, in the early hours of morning in a lovely field. This saint initiated him into the conscious technique of Vihangam Yoga and instructed him not to come out of his cave before the public until a period of six months of ‘anusthan’ was complete. Swamiji later on realized that the saint he had met was not a common one. He was the Eternal True teacher, Nitya Anadi Sadguru. The Lord is Eternal and the way of realizing him is also eternal. As such the teacher teaching the scientific technique of realizing the Lord is also eternal. He is gifted with the power of materializing his body whenever he wills. He also appears in the physical form in all the four ages viz Satyuga, Tretaa, Dwapar and Kaliyuga and teaches the technique of Vihangam Yoga, the only way of God-realization, to deserving aspirants.",
        },
        {
          kind: "paragraph",
          text: "A reference to this effect has been made in the Vedas at several places.",
        },
        {
          kind: "verse",
          lines: [
            "Twa dootam agney amritam yugey- yugey",
            "havyawaaham dadhirey paayum eedayam.",
            "Devaasah cha martasah cha jaagrivim",
            "vibhum vishwapatim namasaa nishedirey.",
          ],
          attribution: "Rig Ved",
        },
        {
          kind: "paragraph",
          text: "O Lord! Your messenger comes to this world with your nectarian message to emancipate the suffering humanity and leads it to Thee. People as well as Deity’s can’t know Him. We lay ourselves prostrate to his Holy feet.",
        },
        {
          kind: "paragraph",
          text: "He practiced Vihangam Yoga for seventeen long years, under the guidance of the same Eternal Sadguru, in his self made cave at Vritikoot Ashram Pakari, Balia, in his cave at Shoonya Shikar Ashram Himalayas, at Jhoonsi Ashram (Prayag) on the bank of Ganges and at several other sacred places. He undertook a number of yoga anusthans (Continuous Practice) lasting continuously for six months. Being pleased with his great sacrifice and perseverance, Nitya Anadi Sadguru endowed him with the authority of True Teacher. In the month of Magh (two months before the Indian Festival- Holi), Eternal Sadguru blessed Maharishi Sadafal Deo Ji with the authority of Sadguru and in the process, transferred the divine Brahma Vidya Knowledge to him. It was that auspicious moment when Sadguru Sadafal Deo Ji was enlightened with the complete Brahma Vidya knowledge. Only a Sadguru has the potential to bless a disciple with the complete knowledge and authorize the disciple as next Sadguru. No one can claim himself or herself as Sadguru just by reading VEDAs and other books. A sadguru is the one who has the practical experience of Brahma Vidya and who can empower a deserved disciple with the same. Sadguru Sadafal Deo Ji Maharaj has said in his unique spiritual work Swarveda",
        },
        {
          kind: "verse",
          lines: [
            "Magh Maas shub maas hai, Ardh kumbh paryaag",
            "Sadguru sadguru pad diye, puran gyaan viraag",
          ],
          attribution: "Swarved",
        },
      ],
    },
    hi: {
      heading: "हिमालय योगी सद्गुरु सदाफलदेव जी महाराज",
      portrait: sadafaldeoPortrait,
      body: [
        {
          kind: "paragraph",
          text: "महर्षि सद्गुरु सदाफलदेव जी महाराज का जन्म विक्रम संवत 1945 के भाद्रपद के प्रथम पखवाड़े की चतुर्थी तिथि को वर्ष 1888 में हुआ था।",
        },
        {
          kind: "paragraph",
          text: "वे महर्षि श्रृंगी के वंश में एक प्रसिद्ध योगी परिवार में पैदा हुए थे। उनके पूर्वज, बाबा लालजी राव, जो उनसे छह पीढ़ी आगे थे, एक महान योगी थे जो लगातार छह महीने तक ध्यान में दृढ़ योग मुद्रा में बैठते थे।",
        },
        {
          kind: "heading",
          text: '"सद्गुरु सदाफलदेव जी महाराज की यात्रा" :—',
        },
        // The old site's Hindi markup runs the en body's three paragraphs
        // above ("Morning shows the day...", "At the early age of
        // eleven...", "One night while he was meditating...") together as
        // one <br>-separated block instead of three; nothing is omitted,
        // only the paragraph break.
        {
          kind: "paragraph",
          text: "सुबह दिन को दिखाती है और बच्चा आदमी को दिखाता है। यह कहावत महर्षि सद्गुरु सदाफलदेव जी महाराज के मामले में बिल्कुल सटीक बैठती है, जब वे दो साल के थे; वे एक लकड़ी के तख्त पर अलग बैठ गए और उनके पिता, माता और उनके घर के अन्य बुजुर्ग सदस्य उनके सामने झुक गए, क्योंकि वे एक महान संत थे। लोगों ने सोचा कि बच्चा मजाक कर रहा है और उन्होंने श्रद्धापूर्वक उसके पैर छुए। वही लड़का जब स्कूल जाने लगा तो उसने अपनी पढ़ाई जारी रखी। वे प्राचीन शास्त्रों के माध्यम से ईश्वर प्राप्ति का रहस्य जानने के लिए संस्कृत सीखना चाहते थे। ग्यारह वर्ष की अल्पायु में सदाफल संस्कृत सीखने के लिए काशी गए, लेकिन काशी के संस्कृत के विद्वान, जो जाति से ब्राह्मण थे, उन्हें संस्कृत का ज्ञान देने के लिए सहमत नहीं हुए, क्योंकि वे क्षत्रिय परिवार से थे। निराश होकर बालक सदाफल ने योग का अभ्यास करने का निर्णय लिया, क्योंकि उन्होंने सुना था कि प्राचीन ऋषियों और मुनियों ने योग का अभ्यास करके प्रत्येक वस्तु का अमूल्य और गहन ज्ञान प्राप्त किया था। परिणामस्वरूप वे भगवान कृष्ण द्वारा गीता में बताई गई 'उन्नमुनिमुद्रा' का अभ्यास करते हुए ध्यान में बैठ गए। महर्षि सद्गुरु सदाफलदेव ने मन को नियंत्रित करने के लिए योग का अपना पहला पाठ लगातार तीन वर्षों तक किया। वे प्रतिदिन छह घंटे सिद्धासन में एकांत स्थान पर ध्यान में बैठते थे। एक रात जब वे इस प्रकार ध्यान कर रहे थे, तो उन्होंने पाया कि उनका कमरा तेजोमय हो गया था। उन्होंने शून्य से एक मधुर और मधुर आवाज भी सुनी। \"इससे तुम्हें परमेश्वर की प्राप्ति नहीं होगी, क्योंकि यह अभ्यास भौतिक इन्द्रियों पर आधारित है।\" कभी-कभी ध्यान की अवस्था में उन्हें ऐसा महसूस होता था कि किसी ने उनके सिर के सबसे ऊपरी भाग पर अपनी एक अंगुली रख दी है। जब उन्होंने अपनी आँखें खोलीं, तो उन्हें वहाँ कोई नहीं मिला। वे समझ नहीं पाए कि किसने इतनी मीठी बात कही थी और किसने उनके सिर को अपनी अंगुली से छुआ था। बाद में जब स्वामीजी ने पाकारी के वृत्तिकूट आश्रम में अपनी गुफा के अंदर योग का अभ्यास करना शुरू किया, तो सुबह के समय एक सुंदर मैदान में उनकी मुलाकात एक तेजोमय संत से हुई। इस संत ने उन्हें विहंगम योग की सचेतन तकनीक में दीक्षित किया और उन्हें निर्देश दिया कि जब तक अनुष्ठान की छह महीने की अवधि पूरी न हो जाए, वे अपनी गुफा से बाहर लोगों के सामने न आएं। बाद में स्वामीजी को एहसास हुआ कि वे जिस संत से मिले थे, वे कोई सामान्य व्यक्ति नहीं थे। वे शाश्वत सच्चे शिक्षक, नित्य अनादि सद्गुरु थे। भगवान शाश्वत हैं और उन्हें महसूस करने का तरीका भी शाश्वत है। इस प्रकार भगवान को महसूस करने की वैज्ञानिक तकनीक सिखाने वाला शिक्षक भी शाश्वत है। उन्हें जब भी चाहें अपने शरीर को मूर्त रूप देने की शक्ति प्राप्त है। वे चारों युगों अर्थात् सतयुग, त्रेता, द्वापर और कलियुग में भौतिक रूप में भी प्रकट होते हैं और योग्य साधकों को ईश्वर प्राप्ति का एकमात्र मार्ग विहंगम योग की विधि सिखाते हैं।",
        },
        {
          kind: "paragraph",
          text: "वेदों में इस आशय का उल्लेख कई स्थानों पर किया गया है।",
        },
        {
          kind: "verse",
          lines: [
            "त्व दूतं अग्नि अमृतं युगे-युगे",
            "हव्यवाहं दधिरे पयुम् ईदयाम्।",
            "देवासः च मर्त्तसः च जाग्रीविम्",
            "विभुं विश्वपतिं नमसा निषेदिरे।",
          ],
          attribution: "ऋग्वेद",
        },
        {
          kind: "paragraph",
          text: "हे प्रभु! आपका दूत पीड़ित मानवता को मुक्ति दिलाने के लिए आपके अमृत संदेश के साथ इस संसार में आता है और इसे आपकी ओर ले जाता है। मनुष्य और देवता दोनों ही उसे नहीं जान सकते। हम उनके पवित्र चरणों में नतमस्तक हैं।",
        },
        {
          kind: "paragraph",
          text: "उन्होंने उसी शाश्वत सद्गुरु के मार्गदर्शन में सत्रह वर्षों तक विहंगम योग का अभ्यास किया, वृत्तिकूट आश्रम पकरी, बलिया में अपनी स्वयं निर्मित गुफा में, शून्य शिकार आश्रम हिमालय की अपनी गुफा में, गंगा के तट पर झूंसी आश्रम (प्रयाग) में तथा कई अन्य पवित्र स्थानों पर। उन्होंने छह महीने तक लगातार कई योग अनुष्ठान (निरंतर अभ्यास) किए। उनके महान त्याग और दृढ़ता से प्रसन्न होकर नित्य अनादि सद्गुरु ने उन्हें सच्चे शिक्षक की उपाधि प्रदान की। माघ के महीने में (भारतीय त्योहार- होली से दो महीने पहले), शाश्वत सद्गुरु ने महर्षि सदाफल देव जी को सद्गुरु की उपाधि प्रदान की और इस प्रक्रिया में, उन्हें दिव्य ब्रह्म विद्या ज्ञान हस्तांतरित किया। यह वह शुभ क्षण था जब सद्गुरु सदाफल देव जी को पूर्ण ब्रह्म विद्या ज्ञान का ज्ञान हुआ। केवल एक सद्गुरु में ही यह क्षमता होती है कि वह किसी शिष्य को पूर्ण ज्ञान प्रदान कर सके और शिष्य को अगला सद्गुरु बना सके। केवल वेद और अन्य पुस्तकें पढ़कर कोई भी स्वयं को सद्गुरु होने का दावा नहीं कर सकता। सद्गुरु वह होता है जिसके पास ब्रह्म विद्या का व्यावहारिक अनुभव होता है और जो एक योग्य शिष्य को उससे सशक्त बना सकता है। सद्गुरु सदगुरु सदाफलदेव जी महाराज ने अपने अद्वितीय आध्यात्मिक ग्रंथ स्वर्वेद में कहा है",
        },
        {
          kind: "verse",
          lines: [
            "माघ मास शुभ मास है, अर्ध कुम्भ परयाग",
            "सदगुरू सदगुरू पद दिये, पूरण ज्ञान विराग",
          ],
          attribution: "स्वर्वेद",
        },
      ],
    },
  },
  "sadguru-dharmchandradeo-ji-maharaj": {
    en: {
      heading: "Sadguru Acharya Shri Dharmachandra Deo Ji Maharaj",
      portrait: dharmchandradeoPortrait,
      body: [
        {
          kind: "paragraph",
          text: "Dharmachandra Deo Ji was born on auspicious day of Maha Shivaratri in 1919 A.D. He was the only son of Sadguru Sadafal Deo Ji Maharaj.",
        },
        {
          kind: "paragraph",
          text: "15 years before He discarded his mortal frame, Sadguru Sadafal Deo Ji Maharaj ceremoniously proclaimed him as his heir in the presence of a large number of disciples on the banks of Holy River the Ganges.",
        },
        {
          kind: "paragraph",
          text: "Brought up in the family tradition of Yogi’s, he grew into an avid reader of Vedas, Upanishads, and Holy Scriptures and went on to become a powerful writer and orator of Brahma Vidya Vihangam Yoga. He was married to Kamla Devi.",
        },
        { kind: "heading", text: '"On a mission"' },
        {
          kind: "paragraph",
          text: "Dharmachandra Deo Ji Maharaj inherited the mantle of Sadguru after Sadguru Sadafal Deo Ji Maharaj left his mortal frame on Feb 11, 1954.",
        },
        {
          kind: "paragraph",
          text: "Marching on, as an enlightened and obedient son and disciple, he devoted all his life to the cause of spreading the divine knowledge of Brahma Vidya Vihangam Yoga soon after assuming the responsibility of Parampara Sadguru after Sadguru Sadafal Deo Ji Maharaj.",
        },
        {
          kind: "paragraph",
          text: "He set upon the task of fulfilling the unfinished agenda of Sadguru Sadafal Deo Ji Maharaj of constructing the five main spiritual centers (ashrams), which had already been established by the Sadguru during His lifetime. These ashrams were envisioned to become the most powerful centers with enormous cosmic vibrations for the physical and spiritual development of true seekers of Brahma Vidya from all over the world.",
        },
        {
          kind: "paragraph",
          text: "However, the most important task that lay before him was to write the commentary of the most revered and unique scripture, “The Swarveda”. It can be safely said “The Swarveda” is the only book of its kind in the whole world that contains all the secrets of Brahmavidya (Brahma Gyan), the Spiritual Science, that hitherto remained completely obscured beneath thick layers of secrecy of the verses of the Vedas, Upanishads and other Holy scriptures. It undisputedly is the only book that clearly enlightens the path leading to the unification of the individual consciousness with the universal consciousness, the Almighty, Omnipresent and Infinite Supreme Soul.",
        },
        {
          kind: "paragraph",
          text: "In its original form the “The Swarveda” would have remained too complex for any one to understand the real import of its highly camouflaged and secret verses. It was Dharmachandra Deo Ji Maharaj alone who had the divine vision and wisdom to bring out the real message underlying the secret verses. Without his commentary, the entire mankind would have remained deprived of the divine light of Brahma Vidya.",
        },
        {
          kind: "paragraph",
          text: "Besides commentary of Swarveda, Dharmachandra Deo Ji Maharaj also wrote ten other spiritual books covering various aspects of Brahma Vidya Vihangam Yoga. All this was in addition to the routine work of publishing the in-house Hindi spiritual monthly, ‘Sadguru Sandesh’ which is now called ‘Vihangam Yoga Sandesh’",
        },
        { kind: "heading", text: "Ashram construction (Spiritual Centers)" },
        {
          kind: "paragraph",
          text: "All these engagements left him with very little time to start the construction work of all the five designated spiritual centers. However, he started the construction work at two locations: –",
        },
        {
          kind: "heading",
          text: "“Vrittikut” ashram in Balia Dist. of Uttar Pradesh State:",
        },
        {
          kind: "paragraph",
          text: "This will always remain the most powerful center in the spiritual world. This is the very place where Sadguru Sadafal Deo Ji Maharaj began his most arduous penance and relentless meditation for 17 long years in a man-made cave. This is where He rediscovered the techniques and the principles of the long lost Brahma Vidya Vihangam Yoga. Sadguru Dharmachandra Deo Ji Maharaj laid the foundation and completed the construction work of main building and the Brahma Vidya Vedi during his life time.",
        },
        {
          kind: "heading",
          text: "“Madhumati” ashram in Gaya town, Gaya Dist, Bihar State:",
        },
        {
          kind: "paragraph",
          text: "The main publication work of the spiritual books, monthly ‘Sadguru Sandesh’ and other literatures were carried from this place.",
        },
        { kind: "heading", text: "Last Days" },
        {
          kind: "paragraph",
          text: "Sadguru Sadafal Deo Ji was endowed with twenty divine, super natural, incredulous spiritual powers viz. He could travel anywhere in space as well as time; He, at His will, could be present at many places simultaneously to protect His disciples and so on. Sadgurudeo, before leaving His physical body, transferred these divine powers to Dharmachandra Deo Ji Maharaj to honor him as the first Parampara Sadguru. He held the divine Sadguru authority for 15 years.",
        },
        {
          kind: "paragraph",
          text: "Sadguru Sadafal Deo Ji Maharaj had earlier instructed him to give up all the works related to ashrams, society and propagation of Brahma Vidya Vihangam Yoga three months before July 28, 1969 (which Sadgurudeo had already declared as the last day of Dharmachandra Deo Ji Maharaj in the mortal frame).He complied with these instructions and establishing himself in Yoga Mudra (Yogic Posture) to leave his mortal body on the prophesied date of 28th July, 1969.",
        },
        {
          kind: "paragraph",
          text: "Prior to his death he had called his third son, Sadguru Swatantra Deo Ji Maharaj and passed on all the secrets of spiritual powers and empowered him as The Sadguru.",
        },
      ],
    },
    hi: {
      heading: "सद्गुरु आचार्य श्री धर्मचन्द्रदेव जी महाराज",
      portrait: dharmchandradeoPortrait,
      body: [
        {
          kind: "paragraph",
          text: "धर्मचन्द्रदेव जी का जन्म महाशिवरात्रि के पावन दिन 1919 ई. में हुआ था। वे सद्गुरु सदाफलदेव जी महाराज के इकलौते पुत्र थे।",
        },
        {
          kind: "paragraph",
          text: "अपने नश्वर शरीर को त्यागने से 15 वर्ष पहले, सद्गुरु सदाफलदेव जी महाराज ने पवित्र नदी गंगा के तट पर बड़ी संख्या में शिष्यों की उपस्थिति में उन्हें अपना उत्तराधिकारी घोषित किया था।",
        },
        {
          kind: "paragraph",
          text: "योगियों की पारिवारिक परंपरा में पले-बढ़े, वे वेदों, उपनिषदों और पवित्र शास्त्रों के एक उत्साही पाठक बन गए और ब्रह्म विद्या विहंगम योग के एक शक्तिशाली लेखक और वक्ता बन गए। उनका विवाह कमला देवी से हुआ था।",
        },
        { kind: "heading", text: '"एक मिशन पर"' },
        {
          kind: "paragraph",
          text: "धर्मचन्द्रदेव जी महाराज ने 11 फरवरी, 1954 को सद्गुरु सदाफलदेव जी महाराज के नश्वर शरीर त्यागने के बाद सद्गुरु की पदवी प्राप्त की।",
        },
        {
          kind: "paragraph",
          text: "सद्गुरु सदाफलदेव जी महाराज के बाद परम्परा सद्गुरु का दायित्व संभालने के तुरंत बाद, एक प्रबुद्ध और आज्ञाकारी पुत्र और शिष्य के रूप में आगे बढ़ते हुए, उन्होंने अपना पूरा जीवन ब्रह्म विद्या विहंगम योग के दिव्य ज्ञान के प्रसार के लिए समर्पित कर दिया।",
        },
        {
          kind: "paragraph",
          text: "उन्होंने सद्गुरु सदाफलदेव जी महाराज के अधूरे एजेंडे को पूरा करने का कार्य शुरू किया, जिसमें पांच मुख्य आध्यात्मिक केंद्रों (आश्रमों) का निर्माण शामिल था, जिन्हें सद्गुरु ने अपने जीवनकाल में ही स्थापित कर लिया था। इन आश्रमों की कल्पना दुनिया भर के सच्चे ब्रह्म विद्या साधकों के भौतिक और आध्यात्मिक विकास के लिए विशाल ब्रह्मांडीय कंपन वाले सबसे शक्तिशाली केंद्र बनने के लिए की गई थी।",
        },
        // The old site's Hindi markup runs the en body's three paragraphs
        // above ("However, the most important task...", "In its original
        // form...", "Besides commentary of Swarveda...") together as one
        // <br>-separated block instead of three; nothing is omitted, only
        // the paragraph break.
        {
          kind: "paragraph",
          text: 'हालांकि, उनके सामने सबसे महत्वपूर्ण कार्य सबसे प्रतिष्ठित और अद्वितीय ग्रंथ, "स्वर्वेद" की व्याख्या लिखना था। यह कहना सुरक्षित होगा कि “स्वर्वेद” पूरी दुनिया में अपनी तरह का एकमात्र ऐसा ग्रंथ है जिसमें ब्रह्मविद्या (ब्रह्मज्ञान) के सभी रहस्य समाहित हैं, जो अब तक वेदों, उपनिषदों और अन्य पवित्र शास्त्रों के श्लोकों की गोपनीयता की मोटी परतों के नीचे पूरी तरह से अस्पष्ट थे। यह निर्विवाद रूप से एकमात्र ऐसा ग्रंथ है जो व्यक्तिगत चेतना को सार्वभौमिक चेतना, सर्वशक्तिमान, सर्वव्यापी और अनंत परमात्मा के साथ एकीकृत करने के मार्ग को स्पष्ट रूप से प्रकाशित करता है। अपने मूल रूप में “स्वर्वेद” इतना जटिल होता कि कोई भी इसके अत्यधिक छद्म और गुप्त श्लोकों के वास्तविक अर्थ को समझ नहीं पाता। यह केवल धर्मचन्द्रदेव जी महाराज ही थे जिनके पास गुप्त श्लोकों में अंतर्निहित वास्तविक संदेश को सामने लाने के लिए दिव्य दृष्टि और ज्ञान था। उनकी व्याख्या के बिना, संपूर्ण मानव जाति ब्रह्म विद्या के दिव्य प्रकाश से वंचित रह जाती। स्वर्वेद की व्याख्या के अलावा धर्मचन्द्रदेव जी महाराज ने ब्रह्म विद्या विहंगम योग के विभिन्न पहलुओं को कवर करने वाली दस अन्य आध्यात्मिक पुस्तकें भी लिखीं। यह सब हिंदी आध्यात्मिक मासिक पत्रिका ‘सद्गुरु संदेश’ के प्रकाशन के नियमित कार्य के अतिरिक्त था, जिसे अब ‘विहंगम योग संदेश’ कहा जाता है।',
        },
        { kind: "heading", text: "आश्रम निर्माण (आध्यात्मिक केंद्र)" },
        {
          kind: "paragraph",
          text: "इन सभी व्यस्तताओं के कारण उन्हें सभी पाँच नामित आध्यात्मिक केंद्रों के निर्माण कार्य को शुरू करने के लिए बहुत कम समय मिला। हालाँकि, उन्होंने दो स्थानों पर निर्माण कार्य शुरू किया: -",
        },
        {
          kind: "heading",
          text: "उत्तर प्रदेश राज्य के बलिया जिले में “वृत्तिकूट” आश्रम:",
        },
        {
          kind: "paragraph",
          text: "यह आध्यात्मिक दुनिया का सबसे शक्तिशाली केंद्र हमेशा रहेगा। यह वही स्थान है जहाँ सद्गुरु सदाफलदेव जी महाराज ने एक मानव निर्मित गुफा में 17 वर्षों तक अपनी सबसे कठिन तपस्या और अथक ध्यान शुरू किया था। यहीं पर उन्होंने लंबे समय से लुप्त ब्रह्म विद्या विहंगम योग की तकनीकों और सिद्धांतों को फिर से खोजा। सद्गुरु धर्मचन्द्रदेव जी महाराज ने अपने जीवनकाल में ही मुख्य भवन और ब्रह्म विद्या वेदी का शिलान्यास और निर्माण कार्य पूरा किया।",
        },
        {
          kind: "heading",
          text: "बिहार राज्य के गया जिले के गया शहर में “मधुमती” आश्रम:",
        },
        {
          kind: "paragraph",
          text: "आध्यात्मिक पुस्तकों, मासिक ‘सद्गुरु संदेश’ और अन्य साहित्य का मुख्य प्रकाशन कार्य यहीं से होता था।",
        },
        { kind: "heading", text: "अंतिम दिन" },
        {
          kind: "paragraph",
          text: "सद्गुरु सदाफलदेव जी को बीस दिव्य, अलौकिक, अविश्वसनीय आध्यात्मिक शक्तियाँ प्राप्त थीं। वे अंतरिक्ष और समय में कहीं भी यात्रा कर सकते थे; वे अपनी इच्छानुसार अपने शिष्यों की रक्षा के लिए एक साथ कई स्थानों पर उपस्थित हो सकते थे, इत्यादि। सद्गुरुदेव ने अपना भौतिक शरीर छोड़ने से पहले, इन दिव्य शक्तियों को धर्मचन्द्रदेव जी महाराज को हस्तांतरित कर दिया, ताकि उन्हें प्रथम परम्परा सद्गुरु के रूप में सम्मानित किया जा सके। उन्होंने 15 वर्षों तक दिव्य सद्गुरु अधिकार को धारण किया।",
        },
        // The old site's Hindi markup runs the en body's two paragraphs
        // above ("Sadguru Sadafal Deo Ji Maharaj had earlier instructed
        // him...", "Prior to his death he had called his third son...")
        // together as one block instead of two; nothing is omitted, only
        // the paragraph break.
        {
          kind: "paragraph",
          text: "सद्गुरु सदाफलदेव जी महाराज ने उन्हें 28 जुलाई 1969 (जिसे सद्गुरुदेव ने धर्मचन्द्रदेव जी महाराज का नश्वर शरीर में अंतिम दिन घोषित कर दिया था) से तीन महीने पहले ही आश्रम, समाज और ब्रह्म विद्या विहंगम योग के प्रचार-प्रसार से संबंधित सभी कार्यों को त्यागने का निर्देश दिया था। उन्होंने इन निर्देशों का पालन किया और 28 जुलाई 1969 की भविष्यवाणी की गई तिथि को अपने नश्वर शरीर को छोड़ने के लिए योग मुद्रा (योगिक मुद्रा) में खुद को स्थापित किया। अपनी मृत्यु से पहले उन्होंने अपने तीसरे पुत्र सद्गुरु स्वतंत्रदेव जी महाराज को बुलाया और आध्यात्मिक शक्तियों के सभी रहस्यों को उन्हें बताकर सद्गुरु के रूप में सशक्त किया।",
        },
      ],
    },
  },
  "sadguru-swatantradeo-ji-maharaj": {
    en: {
      heading: "Sadguru Acharya Shri Swatantra Deo Ji Maharaj",
      portrait: swatantradeoPortrait,
      body: [
        { kind: "heading", text: '"Birth"' },
        {
          kind: "paragraph",
          text: "The world was blessed with his birth on 28th July 1947 in the village of Garhwar in Ballia, Uttar Pradesh, India. With his birth, Swami Sadafal Deo Ji Maharaj called him as Swatantra Deo and disclosed that India will become an independent nation now and the innumerable suffering souls will get salvation from this world full of agonies under the guidance of this child. He is the grandson of Sadguru Sadafal Deo Ji Maharaj and son of Acharya Sri Dharmachandra Deo Ji Maharaj.",
        },
        { kind: "heading", text: '"Childhood"' },
        {
          kind: "paragraph",
          text: "At the age of 5 years only, He started taking interest in reading Vedas and answering the questions from disciples. The disciples were amazed with his extraordinary abilities and knowledge. When he was 16 years old, Swamiji sent him to Panini University at Kashi for learning Sanskrit and Veda. The faculties and students at Kashi were all stunned with his excellence in the subject.",
        },
        { kind: "heading", text: '"Honoured With Heritage"' },
        {
          kind: "paragraph",
          text: "In 1969, The then Sadguru and his father, Acharya Sri Dharmachandra Deo Ji Maharaj handed over the secret knowledge, power and Sadguru-authority to Sri Swatantra Deo Ji and honored him as second Parampara Sadguru, when he was just 22. Sri Swatantra Deo Ji started the mission of spreading the Brahma Vidya knowledge to the needy souls with complete dedication towards fulfilling Sri Swamiji’s pledge. In the beginning, Sri Swatantra Deo Ji had several challenges in hand. In all odd conditions, Sri Swatantra Deo Ji continued his mission without a slight detoriation in his dedication.",
        },
        {
          kind: "verse",
          lines: [
            "Brahma Vidya hai Brahmaja, Hai yah Ishwariya Dhaar!",
            "Dabei na saari shakti se ,Sadguru Jagat Prachaar !!",
          ],
        },
        {
          kind: "paragraph",
          text: "(The Brahma Vidya originates from the Supreme Lord Himself; it flows from the Lord Himself. Nothing in the world can suppress this from getting spread in the entire world).",
        },
        { kind: "heading", text: '"On the mission"' },
        {
          kind: "paragraph",
          text: "Endowed with twenty kalas (divine spiritual powers), the spiritual master Acharya Sri Swatantra Deo Ji Maharaj is the present Sadguru in physical form. The spiritual power of none other than The Eternal Sadguru himself flows constantly into his soul. With extraordinary abilities and Second Parampara Sadguru Authority, He is spreading the knowledge of Brahma Vidya all over the world so as to fulfill the pledge taken by Swami Sadafal Deo Ji Maharaj for the salvation of minimum 1 lakh of eligible souls.",
        },
        {
          kind: "paragraph",
          text: "The power and knowledge of The Eternal Sadguru is flowing through his body. Sadguru Sri Swatantra Deo Ji Maharaj is endowed with supernatural powers but it is the greatness of Sri Swamiji that he believes in living as per the law of nature and presents himself like any other ordinary person. An ordinary person will not be able to know his divine abilities by his simple appearance.",
        },
        {
          kind: "paragraph",
          text: "“Sadguru mein akarshan bhaari (Sadguru has magnetic personality)” – this Swarveda mantra is the best test to identify a Sadguru and when Sri Swatantra Deo Ji starts his Amritvani from the zone of Akshar Brahma, nothing remains unveiled. The conscious energy of deserving disciples start pulling upwards; many start shaking thanks to extreme pull by Kundalini power; many true seekers get the answers of their unquenched thirst and queries that never opened up!",
        },
        {
          kind: "verse",
          lines: [
            "Saar Shabd Guru Ek hai, Yaamein Bhed na maan!",
            "Bhed Maan Bhavkoop parei, Nirbhedi Nirvaan!!",
          ],
        },
      ],
    },
    hi: {
      heading: "सद्गुरु आचार्य श्री स्वतंत्रदेव जी महाराज",
      portrait: swatantradeoPortrait,
      body: [
        { kind: "heading", text: '"जन्म"' },
        {
          kind: "paragraph",
          text: "भारत के उत्तर प्रदेश के बलिया जिले के गढ़वार गांव में 28 जुलाई 1947 को जन्मे स्वामी सदाफलदेव जी महाराज ने उन्हें स्वतंत्रदेव नाम से पुकारा और बताया कि अब भारत एक स्वतंत्र राष्ट्र बनेगा और इस बालक के मार्गदर्शन में असंख्य पीड़ित आत्माओं को इस पीड़ा भरी दुनिया से मुक्ति मिलेगी। वे सद्गुरु सदाफलदेव जी महाराज के पौत्र और आचार्य श्री धर्मचंद्रदेव जी महाराज के पुत्र हैं।",
        },
        { kind: "heading", text: '"बचपन"' },
        {
          kind: "paragraph",
          text: "केवल 5 वर्ष की आयु में ही वे वेदों को पढ़ने और शिष्यों के प्रश्नों के उत्तर देने में रुचि लेने लगे थे। शिष्य उनकी असाधारण क्षमताओं और ज्ञान से चकित थे। जब वे 16 वर्ष के थे, तो स्वामी जी ने उन्हें संस्कृत और वेद सीखने के लिए काशी के पाणिनि विश्वविद्यालय में भेजा। काशी के संकाय और छात्र सभी इस विषय में उनकी उत्कृष्टता से चकित थे।",
        },
        { kind: "heading", text: '"विरासत से सम्मानित"' },
        {
          kind: "paragraph",
          text: "1969 में तत्कालीन सद्गुरु और उनके पिता आचार्य श्री धर्मचंद्रदेव जी महाराज ने श्री स्वतंत्रदेव जी को गुप्त ज्ञान, शक्ति और सद्गुरु-सत्ता सौंप दी और उन्हें दूसरे परम्परा सद्गुरु के रूप में सम्मानित किया, जब वे मात्र 22 वर्ष के थे। श्री स्वतंत्रदेव जी ने श्री स्वामीजी के संकल्प को पूरा करने के लिए पूरी लगन के साथ जरूरतमंद आत्माओं तक ब्रह्म विद्या के ज्ञान को फैलाने का मिशन शुरू किया। शुरुआत में, श्री स्वतंत्रदेव जी के सामने कई चुनौतियाँ थीं। सभी विषम परिस्थितियों में, श्री स्वतंत्रदेव जी ने अपने समर्पण में थोड़ी भी कमी किए बिना अपने मिशन को जारी रखा।",
        },
        {
          kind: "verse",
          lines: [
            "ब्रह्म विद्या है ब्रह्माजा, है यह ईश्वरीय धार!",
            "दबे न सारी शक्ति से, सद्गुरु जगत प्रचार!!",
          ],
        },
        {
          kind: "paragraph",
          text: "(ब्रह्म विद्या स्वयं परमेश्वर से उत्पन्न होती है; यह स्वयं परमेश्वर से प्रवाहित होती है। दुनिया की कोई भी चीज़ इसे पूरे विश्व में फैलने से नहीं रोक सकती)।",
        },
        { kind: "heading", text: '"मिशन पर"' },
        {
          kind: "paragraph",
          text: "बीस कलाओं (दिव्य आध्यात्मिक शक्तियों) से संपन्न, आध्यात्मिक गुरु आचार्य श्री स्वतंत्रदेव जी महाराज भौतिक रूप में वर्तमान सद्गुरु हैं। उनकी आत्मा में स्वयं सद्गुरु की आध्यात्मिक शक्ति निरंतर प्रवाहित होती रहती है। असाधारण क्षमताओं और द्वितीय परम्परा सद्गुरु प्राधिकरण के साथ, वे कम से कम 1 लाख योग्य आत्माओं के उद्धार के लिए स्वामी सदाफलदेव जी महाराज द्वारा लिए गए संकल्प को पूरा करने के लिए पूरे विश्व में ब्रह्म विद्या का ज्ञान फैला रहे हैं।",
        },
        {
          kind: "paragraph",
          text: "सद्गुरु की शक्ति और ज्ञान उनके शरीर में प्रवाहित हो रहा है। सद्गुरु श्री स्वतंत्रदेव जी महाराज अलौकिक शक्तियों से संपन्न हैं, लेकिन यह श्री स्वामीजी की महानता है कि वे प्रकृति के नियमों के अनुसार जीने में विश्वास करते हैं और खुद को किसी भी अन्य सामान्य व्यक्ति की तरह प्रस्तुत करते हैं। एक साधारण व्यक्ति उनकी दिव्य क्षमताओं को उनके साधारण रूप से नहीं जान पाएगा।",
        },
        {
          kind: "paragraph",
          text: '"सद्गुरु में आकर्षण भारी" - यह स्वर्वेद मंत्र सद्गुरु की पहचान करने की सबसे अच्छी कसौटी है और जब श्री स्वतंत्रदेव जी अक्षर ब्रह्म के क्षेत्र से अपनी अमृतवाणी शुरू करते हैं, तो कुछ भी अनावृत नहीं रह जाता। योग्य शिष्यों की चेतना ऊर्जा ऊपर की ओर खिंचने लगती है; कई लोग कुंडलिनी शक्ति के अत्यधिक खिंचाव के कारण हिलने लगते हैं; कई सच्चे साधकों को उनकी अतृप्त प्यास और कभी न खुल पाने वाले प्रश्नों के उत्तर मिल जाते हैं!',
        },
        {
          kind: "verse",
          lines: [
            "सार शब्द गुरु एक है, यामें भेद न मान!",
            "भेद मान भावुकोप परी, निर्भेद निर्वाण!!",
          ],
        },
      ],
    },
  },
} satisfies Record<SadguruSlug, SadguruBio>;

/** True narrowing for a router-matched slug, so callers never need `as`. */
export function isSadguruSlug(value: string): value is SadguruSlug {
  return Object.hasOwn(BIOS, value);
}
